// Shared input primitives for the Deal Calc panels.
//
// The React reference (CurrencyInput.jsx, NumInput.jsx) uses
// `type="text" inputMode="decimal"` so the displayed string can be
// thousands-separator-formatted (1,000,000) while the mobile keyboard
// stays numeric. The raw value is stripped of non-digits on input.
// We mirror that exactly so users see commas while typing without
// losing the live update path through host.update().

export interface ICurrencyInputOpts {
	value: number;
	onChange: (next: number) => void;
	disabled?: boolean;
	placeholder?: string;
	size?: 'small' | 'default' | 'large';
	// Stable identifier set as data-focus-key on the inner input.
	// Used by captureFocusKey/restoreFocusKey to preserve user focus
	// across panel re-renders that destroy and recreate the row.
	focusKey?: string;
}

export interface INumInputOpts {
	value: number;
	onChange: (next: number) => void;
	disabled?: boolean;
	placeholder?: string;
	dark?: boolean;
	focusKey?: string;
}

// Focus-restoration helpers — panels that re-render rows containing
// inputs lose the user's focus (and selection) on every keystroke
// because the input element is destroyed and replaced. The fix:
// before render, capture which input was focused (by data-focus-key)
// and where the cursor was; after render, find the new equivalent
// input and restore both.

export interface IFocusSnapshot {
	key: string;
	start: number;
	end: number;
}

export function captureFocusKey(scope: HTMLElement | null): IFocusSnapshot | null {
	if (!scope) {
		return null;
	}
	const active = document.activeElement as HTMLInputElement | null;
	if (!active || !scope.contains(active)) {
		return null;
	}
	const key = active.getAttribute('data-focus-key');
	if (!key) {
		return null;
	}
	return {
		key,
		start: typeof active.selectionStart === 'number' ? active.selectionStart : 0,
		end: typeof active.selectionEnd === 'number' ? active.selectionEnd : 0,
	};
}

export function restoreFocusKey(scope: HTMLElement | null, snapshot: IFocusSnapshot | null): void {
	if (!scope || !snapshot) {
		return;
	}
	const target = scope.querySelector<HTMLInputElement>(
		`[data-focus-key="${cssAttrEscape(snapshot.key)}"]`,
	);
	if (!target) {
		return;
	}
	target.focus();
	try {
		target.setSelectionRange(snapshot.start, snapshot.end);
	} catch {
		// Some input types reject setSelectionRange (e.g. type=range).
	}
}

function cssAttrEscape(s: string): string {
	return s.replace(/["\\]/g, '\\$&');
}

// Slider drag guard — wires pointerdown/up listeners on the slider so
// the panel can suppress its own render() while the user is dragging.
// Without this, every `input` event during drag triggers a render that
// destroys and recreates the slider element, breaking the drag and
// pinning the thumb to single-step movements.
//
// Usage:
//   const guard = bindSliderDragGuard(sliderEl);
//   slider.addEventListener('input', () => {
//     // host.update(...)
//     if (!guard.isDragging()) this.render();
//   });
//   guard.onDragEnd(() => this.render());
export interface ISliderDragGuard {
	isDragging(): boolean;
	onDragEnd(cb: () => void): void;
}

export function bindSliderDragGuard(slider: HTMLInputElement): ISliderDragGuard {
	let dragging = false;
	const endCallbacks: Array<() => void> = [];

	const finish = () => {
		document.removeEventListener('pointerup', finish);
		document.removeEventListener('pointercancel', finish);
		if (dragging) {
			dragging = false;
			endCallbacks.forEach(cb => cb());
		}
	};

	slider.addEventListener('pointerdown', () => {
		dragging = true;
		document.addEventListener('pointerup', finish);
		document.addEventListener('pointercancel', finish);
	});

	return {
		isDragging: () => dragging,
		onDragEnd: cb => {
			endCallbacks.push(cb);
		},
	};
}

function formatThousands(n: number): string {
	if (!n || !Number.isFinite(n)) {
		return '';
	}
	return Number(n).toLocaleString('en-US', {maximumFractionDigits: 4});
}

// Strip non-numeric characters and collapse multiple decimal points
// to the first one. Returns the cleaned string the user sees in the
// input; this preserves partial decimals like "0", "0.", "0.5" so
// users can type fractional values without the formatter wiping them.
function cleanNumericTyping(raw: string): string {
	const stripped = raw.replace(/[^\d.]/g, '');
	const firstDot = stripped.indexOf('.');
	if (firstDot < 0) {
		return stripped;
	}
	return stripped.slice(0, firstDot + 1) + stripped.slice(firstDot + 1).replace(/\./g, '');
}

// Apply the typed-value normalization. While the user is mid-typing,
// only thousands-separator commas are inserted/removed; partial
// decimals like "0", "0.", "0.5" stay verbatim. The full canonical
// reformat (including trailing zero handling) lives on blur.
function reformatDuringTyping(input: HTMLInputElement): number {
	const cleaned = cleanNumericTyping(input.value);
	if (input.value !== cleaned) {
		const start = input.selectionStart || 0;
		const removed = input.value.length - cleaned.length;
		input.value = cleaned;
		try {
			input.setSelectionRange(Math.max(0, start - removed), Math.max(0, start - removed));
		} catch {
			// ignored
		}
	}
	const parsed = parseFloat(cleaned);
	const next = Number.isFinite(parsed) ? parsed : 0;

	// Only auto-add commas for whole-integer values past 1000 — a
	// pending decimal entry ("12.", "1234.5") shouldn't be collapsed
	// back into a comma-formatted form mid-keystroke.
	const hasDecimal = cleaned.indexOf('.') >= 0;
	if (!hasDecimal && next >= 1000) {
		const display = next.toLocaleString('en-US');
		if (input.value !== display) {
			const start = input.selectionStart || 0;
			const inserted = display.length - input.value.length;
			input.value = display;
			try {
				input.setSelectionRange(start + inserted, start + inserted);
			} catch {
				// ignored
			}
		}
	}
	return next;
}

// Canonical reformat for blur — drop trailing dot, add thousands
// commas, preserve up to 4 fractional digits. Empties the field when
// the typed value parses to 0 so the placeholder shows again.
//
// The current input.value may already be comma-formatted ("6,000")
// from the live typing reformat, so strip non-numeric chars before
// parseFloat — otherwise parseFloat stops at the first comma and
// silently truncates the value (parseFloat("6,000") === 6).
function reformatOnBlur(input: HTMLInputElement): void {
	const cleaned = cleanNumericTyping(input.value);
	const parsed = parseFloat(cleaned);
	if (!Number.isFinite(parsed) || parsed === 0) {
		input.value = '';
		return;
	}
	input.value = parsed.toLocaleString('en-US', {maximumFractionDigits: 4});
}

// Currency input — wraps a $-prefix in a flex row, formats with commas.
export function currencyInput(opts: ICurrencyInputOpts): HTMLElement {
	const wrap = document.createElement('div');
	const sizeClass = opts.size === 'large' ? ' lb-deal-currency-input--large'
		: opts.size === 'small' ? ' lb-deal-currency-input--small'
		: '';
	wrap.className = `lb-deal-currency-input${sizeClass}${opts.disabled ? ' lb-deal-currency-input--disabled' : ''}`;

	const prefix = document.createElement('span');
	prefix.className = 'lb-deal-currency-input__prefix';
	prefix.textContent = '$';
	wrap.appendChild(prefix);

	const input = document.createElement('input');
	input.type = 'text';
	input.inputMode = 'decimal';
	input.className = 'lb-deal-currency-input__input';
	input.value = formatThousands(opts.value);
	if (opts.disabled) {
		input.disabled = true;
	}
	if (opts.placeholder) {
		input.placeholder = opts.placeholder;
	}
	if (opts.focusKey) {
		input.setAttribute('data-focus-key', opts.focusKey);
	}
	input.addEventListener('input', () => {
		opts.onChange(reformatDuringTyping(input));
	});
	input.addEventListener('blur', () => {
		reformatOnBlur(input);
	});

	wrap.appendChild(input);
	return wrap;
}

// Section primitive — wraps a panel root in a click-to-collapse
// header (Material icon + title + subtitle + chevron) and a body
// container the caller fills with content. Mirrors Layout.jsx's
// Section component from the React standalone.

export interface IPanelSectionOpts {
	rootEl: HTMLElement;
	icon: string; // material-icons name
	title: string;
	subtitle?: string;
	defaultCollapsed?: boolean;
}

export interface IPanelSectionHandles {
	bodyEl: HTMLElement;
	titleEl: HTMLElement;
	subtitleEl: HTMLElement;
	setCollapsed(next: boolean): void;
}

export function panelSection(opts: IPanelSectionOpts): IPanelSectionHandles {
	const root = opts.rootEl;
	while (root.firstChild) {
		root.removeChild(root.firstChild);
	}

	const header = document.createElement('button');
	header.type = 'button';
	header.className = 'lb-deal-section__head';
	header.setAttribute('aria-expanded', String(!opts.defaultCollapsed));

	const iconEl = document.createElement('span');
	iconEl.className = 'lb-deal-section__icon material-icons';
	iconEl.setAttribute('aria-hidden', 'true');
	iconEl.textContent = opts.icon;
	header.appendChild(iconEl);

	const headInfo = document.createElement('div');
	headInfo.className = 'lb-deal-section__head-info';
	const titleEl = document.createElement('h2');
	titleEl.className = 'lb-deal-section__title';
	titleEl.textContent = opts.title;
	headInfo.appendChild(titleEl);
	const subtitleEl = document.createElement('p');
	subtitleEl.className = 'lb-deal-section__subtitle';
	subtitleEl.textContent = opts.subtitle || '';
	headInfo.appendChild(subtitleEl);
	header.appendChild(headInfo);

	const chevron = document.createElement('span');
	chevron.className = 'lb-deal-section__chevron material-icons';
	chevron.setAttribute('aria-hidden', 'true');
	chevron.textContent = 'expand_less';
	header.appendChild(chevron);

	const body = document.createElement('div');
	body.className = 'lb-deal-section__body';

	let collapsed = !!opts.defaultCollapsed;
	const apply = () => {
		root.classList.toggle('lb-deal-section--collapsed', collapsed);
		header.setAttribute('aria-expanded', String(!collapsed));
		body.style.display = collapsed ? 'none' : '';
	};
	header.addEventListener('click', () => {
		collapsed = !collapsed;
		apply();
	});

	root.appendChild(header);
	root.appendChild(body);
	apply();

	return {
		bodyEl: body,
		titleEl,
		subtitleEl,
		setCollapsed(next: boolean): void {
			collapsed = next;
			apply();
		},
	};
}

// Plain numeric input — same comma-display pattern, no $ prefix. Used
// for sqft, beds, baths, percents (when not the deferred-format
// PercentInput in Exit), monthly carry amounts, etc.
export function numInput(opts: INumInputOpts): HTMLInputElement {
	const input = document.createElement('input');
	input.type = 'text';
	input.inputMode = 'decimal';
	input.className = `lb-deal-num-input${opts.dark ? ' lb-deal-num-input--dark' : ''}`;
	input.value = formatThousands(opts.value);
	if (opts.disabled) {
		input.disabled = true;
	}
	if (opts.placeholder) {
		input.placeholder = opts.placeholder;
	}
	if (opts.focusKey) {
		input.setAttribute('data-focus-key', opts.focusKey);
	}
	input.addEventListener('input', () => {
		opts.onChange(reformatDuringTyping(input));
	});
	input.addEventListener('blur', () => {
		reformatOnBlur(input);
	});
	return input;
}
